import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import Badge from '../../components/common/Badge';
import {
  HiOutlineUserCircle, HiOutlineCheck, HiOutlineExclamation,
  HiOutlineLockClosed, HiOutlineEye, HiOutlineShieldCheck,
  HiOutlineBriefcase, HiOutlineCalendar, HiOutlineCash,
  HiOutlinePhone, HiOutlineMail, HiOutlineRefresh,
} from 'react-icons/hi';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    current_password: '',
    password: '',
    confirm_password: '',
  });

  // Hydrate the form whenever the logged-in user object changes
  // (e.g. immediately after a fresh login or after the user updates themself).
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        // Always reset password fields when switching users / after save.
        current_password: '',
        password: '',
        confirm_password: '',
      }));
    }
  }, [user]);

  if (!user) return null;

  const validate = () => {
    if (!form.name.trim()) return 'নাম আবশ্যক।';
    if (form.password) {
      if (form.password.length < 4) return 'নতুন পাসওয়ার্ড কমপক্ষে ৪ অক্ষর হতে হবে।';
      if (form.password !== form.confirm_password) return 'নতুন পাসওয়ার্ড দুইবার একই হতে হবে।';
      if (!form.current_password) return 'পাসওয়ার্ড পরিবর্তন করতে বর্তমান পাসওয়ার্ড দিন।';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      };
      if (form.password) {
        payload.current_password = form.current_password;
        payload.password = form.password;
      }
      const { data } = await API.put('/auth/profile', payload);
      // Refresh AuthContext so the new name/email/phone show up everywhere
      if (data.user && updateUser) updateUser(data.user);
      setSuccess('প্রোফাইল সফলভাবে আপডেট হয়েছে।');
      setForm((f) => ({ ...f, current_password: '', password: '', confirm_password: '' }));
      // Auto-dismiss success after a few seconds
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'সার্ভার ত্রুটি।');
    } finally {
      setSaving(false);
    }
  };

  const passwordMismatch =
    form.password && form.confirm_password && form.password !== form.confirm_password;
  const passwordTooShort = form.password && form.password.length < 4;

  return (
    <div className="space-y-5">
      <PageHeader
        title="প্রোফাইল"
        subtitle="আপনার ব্যক্তিগত তথ্য ও পাসওয়ার্ড আপডেট করুন"
      />

      {/* Profile header card */}
      <div className={
        'card flex items-center gap-4 sm:gap-5 border ' +
        (user.role === 'admin'
          ? 'bg-gradient-to-r from-amber-50 to-amber-100/40 dark:from-amber-900/20 dark:to-amber-900/10 border-amber-200 dark:border-amber-800'
          : 'bg-gradient-brand-soft border-brand-200 dark:border-brand-800')
      }>
        <div className={
          'w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-3xl shadow-pop ' +
          (user.role === 'admin'
            ? 'bg-gradient-to-br from-amber-500 to-amber-700'
            : 'bg-gradient-brand')
        }>
          {user.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold text-ink-900 dark:text-white truncate">
            {user.name}
          </h2>
          <p className="text-sm text-ink-500 dark:text-ink-400 mt-0.5 flex items-center gap-2 flex-wrap">
            {user.role === 'admin' ? (
              <Badge variant="warning" dot>👑 অ্যাডমিন</Badge>
            ) : (
              <Badge variant="success" dot>👷 ওয়ার্কার</Badge>
            )}
            {user.is_active
              ? <Badge variant="success">সক্রিয়</Badge>
              : <Badge variant="neutral">নিষ্ক্রিয়</Badge>}
            {user.phone && <span>📞 {user.phone}</span>}
            {user.email && <span>✉️ {user.email}</span>}
          </p>
        </div>
      </div>

      {/* Two-column layout (responsive): form + side info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: form */}
        <form onSubmit={handleSubmit} className="card lg:col-span-2 space-y-5">
          {error && (
            <div className="p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-xl text-rose-700 dark:text-rose-200 text-sm flex items-start gap-2">
              <HiOutlineExclamation className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-700 dark:text-emerald-200 text-sm flex items-center gap-2 animate-fade-in">
              <HiOutlineCheck className="w-5 h-5" />
              <span>{success}</span>
            </div>
          )}

          {/* Personal info */}
          <div>
            <h3 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider mb-3 flex items-center gap-2">
              <HiOutlineUserCircle className="w-5 h-5 text-brand-600" />
              ব্যক্তিগত তথ্য
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">
                  নাম <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="input-label flex items-center gap-1.5">
                  <HiOutlinePhone className="w-4 h-4" />
                  ফোন
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-field"
                  placeholder="01XXXXXXXXX"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="input-label flex items-center gap-1.5">
                  <HiOutlineMail className="w-4 h-4" />
                  ইমেইল
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-field"
                  placeholder="you@example.com"
                />
              </div>
            </div>
          </div>

          {/* Password change */}
          <div className="pt-5 border-t border-ink-100 dark:border-ink-700">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider mb-1 flex items-center gap-2">
              <HiOutlineLockClosed className="w-5 h-5 text-violet-600" />
              পাসওয়ার্ড পরিবর্তন
            </h3>
            <p className="text-xs text-ink-500 dark:text-ink-400 mb-3">
              🔒 পাসওয়ার্ড পরিবর্তন করতে নিচে নতুন পাসওয়ার্ড দিন। না দিলে আপনার পুরানো পাসওয়ার্ড থাকবে।
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="input-label">বর্তমান পাসওয়ার্ড</label>
                <div className="relative">
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={form.current_password}
                    onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                    className="input-field pr-12"
                    placeholder="পাসওয়ার্ড পরিবর্তন করতে দিন"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                    title={showCurrentPw ? 'লুকান' : 'দেখান'}
                  >
                    {showCurrentPw ? <HiOutlineEye className="w-5 h-5" /> : <span>👁️</span>}
                  </button>
                </div>
              </div>
              <div>
                <label className="input-label">নতুন পাসওয়ার্ড</label>
                <div className="relative">
                  <input
                    type={showNewPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={
                      'input-field pr-12 ' +
                      (passwordTooShort
                        ? 'border-rose-400 focus:ring-rose-500'
                        : form.password
                        ? 'border-emerald-400 focus:ring-emerald-500'
                        : '')
                    }
                    placeholder="কমপক্ষে ৪ অক্ষর"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                  >
                    {showNewPw ? <HiOutlineEye className="w-5 h-5" /> : <span>👁️</span>}
                  </button>
                </div>
                {form.password && (
                  <p className={
                    'text-[11px] mt-1 ' +
                    (passwordTooShort ? 'text-rose-600' : 'text-emerald-600')
                  }>
                    {passwordTooShort ? '⚠️ খুব ছোট' : '✓ শক্তিশালী যথেষ্ট'}
                  </p>
                )}
              </div>
              <div>
                <label className="input-label">নিশ্চিত করুন</label>
                <input
                  type={showNewPw ? 'text' : 'password'}
                  value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  className={
                    'input-field ' +
                    (passwordMismatch
                      ? 'border-rose-400 focus:ring-rose-500'
                      : form.confirm_password && form.password === form.confirm_password
                      ? 'border-emerald-400 focus:ring-emerald-500'
                      : '')
                  }
                  placeholder="একই পাসওয়ার্ড আবার"
                  autoComplete="new-password"
                />
                {passwordMismatch && (
                  <p className="text-[11px] mt-1 text-rose-600">⚠️ পাসওয়ার্ড দুটো মিলছে না।</p>
                )}
                {form.confirm_password && form.password === form.confirm_password && (
                  <p className="text-[11px] mt-1 text-emerald-600">✓ মিলেছে</p>
                )}
              </div>
            </div>
          </div>

          {/* Sticky-ish save bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-ink-100 dark:border-ink-700">
            <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1.5">
              <HiOutlineShieldCheck className="w-4 h-4 text-emerald-600" />
              পরিবর্তন সংরক্ষণ করলে সব জায়গায় (sidebar, header) reflect হবে।
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({
                  name: user.name || '',
                  email: user.email || '',
                  phone: user.phone || '',
                  current_password: '', password: '', confirm_password: '',
                })}
                disabled={saving}
                className="btn-secondary"
              >
                <HiOutlineRefresh className="w-4 h-4" />
                রিসেট
              </button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>সংরক্ষণ হচ্ছে...</span>
                  </>
                ) : (
                  <>
                    <HiOutlineCheck className="w-5 h-5" />
                    <span>পরিবর্তন সংরক্ষণ করুন</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Right: account info sidebar */}
        <aside className="space-y-4">
          <div className="card">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider mb-3 flex items-center gap-2">
              <HiOutlineBriefcase className="w-5 h-5 text-accent-600" />
              অ্যাকাউন্ট তথ্য
            </h3>
            <dl className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <dt className="text-ink-500 dark:text-ink-400 w-24 flex-shrink-0">Role</dt>
                <dd className="font-semibold text-ink-900 dark:text-white">
                  {user.role === 'admin' ? '👑 অ্যাডমিন' : '👷 ওয়ার্কার'}
                </dd>
              </div>
              <div className="flex items-start gap-2">
                <dt className="text-ink-500 dark:text-ink-400 w-24 flex-shrink-0">Status</dt>
                <dd className="font-semibold text-ink-900 dark:text-white">
                  {user.is_active ? '✅ সক্রিয়' : '⛔ নিষ্ক্রিয়'}
                </dd>
              </div>
              <div className="flex items-start gap-2">
                <dt className="text-ink-500 dark:text-ink-400 w-24 flex-shrink-0">Daily Salary</dt>
                <dd className="font-semibold text-ink-900 dark:text-white">
                  ৳{Number(user.daily_salary || 0).toLocaleString('en-IN')}
                </dd>
              </div>
              <div className="flex items-start gap-2">
                <dt className="text-ink-500 dark:text-ink-400 w-24 flex-shrink-0">User ID</dt>
                <dd className="font-mono text-ink-900 dark:text-white">#{user.id}</dd>
              </div>
            </dl>
          </div>

          <div className="card bg-ink-50 dark:bg-ink-900/30 border-ink-200 dark:border-ink-700">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider mb-2 flex items-center gap-2">
              <HiOutlineCalendar className="w-5 h-5 text-ink-500" />
              অ্যাকাউন্ট সময়কাল
            </h3>
            <p className="text-xs text-ink-500 dark:text-ink-400">
              যোগদান: {user.created_at ? new Date(user.created_at).toLocaleDateString('bn-BD', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
            </p>
          </div>

          {user.role === 'worker' && (
            <div className="card bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800">
              <h3 className="text-sm font-semibold text-brand-900 dark:text-brand-200 uppercase tracking-wider mb-2 flex items-center gap-2">
                <HiOutlineCash className="w-5 h-5" />
                দ্রুত অ্যাকশন
              </h3>
              <ul className="space-y-1.5 text-xs text-brand-800 dark:text-brand-300">
                <li>• আজকের হাজিরা mark করুন</li>
                <li>• অগ্রিম request করুন</li>
                <li>• বেতন slip দেখুন</li>
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Profile;